import React, { useState } from "react";
import WithSnowFlakes from "../../../components/LandingPage/WithSnowFlakes";

const DeviceSettingsPage = () => {
  return (
    <WithSnowFlakes>
      <div className="flex flex-col w-full items-center px-4 py-20 overflow-scroll">
        <div className="w-full max-w-4xl">
          <h1 className="text-snow-white font-bold text-5xl text-center font-catamaran mb-6">
            Configuring Android & iPhone Devices for SnowScape
          </h1>

          <div className="text-snow-white text-lg leading-relaxed font-noto mb-12 bg-snow-white bg-opacity-5 border-l-4 border-snow-green p-6 rounded">
            <p className="mb-4">
              SnowScape works well on any recent Android or iPhone device.
              However, optimal location tracking requires a few configuration
              steps on your device. These settings ensure that SnowScape can
              accurately track crew locations and provide real-time job status
              updates.
            </p>
            <p>
              Follow the sections below to configure your device for the best
              experience.
            </p>
          </div>

          {/* Allow Location / Foreground Location */}
          <SettingSection
            id="allow-location"
            title="Allow Location Access"
            description="This is the basic permission that allows SnowScape to access your device's location. Without this, the app cannot track location at all."
            iosSteps={[
              "Open Settings on your iOS device",
              "Scroll down and tap 'Privacy & Security'",
              "Tap 'Location Services'",
              "Find SnowScape in the list",
              "Tap SnowScape and select 'Allow While Using'",
            ]}
            androidSteps={[
              "Open Settings on your Android device",
              "Scroll down and tap 'Apps' or 'Applications'",
              "Find and tap 'SnowScape'",
              "Tap 'Permissions'",
              "Tap 'Location' and select 'Allow all the time'",
            ]}
          />

          {/* Background Location */}
          <SettingSection
            id="background-location"
            title="Background Location Access"
            description="Background location allows SnowScape to track your location even when the app is not actively in use. This is essential for features like crew location tracking and geofencing while you're working on a route."
            iosSteps={[
              "Open Settings on your iOS device",
              "Scroll down and tap 'Privacy & Security'",
              "Tap 'Location Services'",
              "Find SnowScape in the list",
              "Tap SnowScape and select 'Always'",
            ]}
            androidSteps={[
              "Open Settings on your Android device",
              "Scroll down and tap 'Apps' or 'Applications'",
              "Find and tap 'SnowScape'",
              "Tap 'Permissions'",
              "Tap 'Location' and select 'Allow all the time'",
            ]}
          />

          {/* Location Enabled on Device */}
          <SettingSection
            id="location-enabled"
            title="Enable Location Services on Your Device"
            description="Your device's location services must be turned on system-wide for any app to access location data. This is a device-level setting, not specific to SnowScape."
            iosSteps={[
              "Open Settings on your iOS device",
              "Tap 'Privacy & Security'",
              "Tap 'Location Services'",
              "Ensure the Location Services toggle is ON (green)",
              "Scroll down to find SnowScape and ensure it's set to 'Always'",
            ]}
            androidSteps={[
              "Open Settings on your Android device",
              "Scroll down and tap 'Location'",
              "Ensure the Location toggle is ON",
              "Tap 'Location Services' or 'Mode'",
              "Select 'High accuracy' to enable GPS, Wi-Fi, and mobile network positioning",
            ]}
          />

          {/* Battery Optimizations */}
          <SettingSection
            id="battery-optimizations"
            title="Disable Battery Optimizations for SnowScape"
            description="Battery optimization settings can limit background app activity to save power. Disabling these for SnowScape ensures the app can run reliably in the background while you're on a route."
            androidSteps={[
              "Open Settings on your Android device",
              "Search for 'Battery' or 'Battery optimization' in the Settings search bar",
              "Tap 'Battery optimization' or 'Advanced battery options'",
              "Look for 'Apps using battery optimization' or 'Battery saver'",
              "Find SnowScape in the list",
              "Tap it and select 'Don't optimize' or 'Unrestricted'",
              "Confirm the change",
            ]}
          />

          {/* Foreground Service / Sticky Notification */}
          <SettingSection
            id="foreground-service"
            title="Allow Foreground Service Notification"
            description="When you're actively using SnowScape on a route, the app displays a sticky notification showing that location tracking is active. This is required on Android to keep the app running reliably in the background."
            androidSteps={[
              "When SnowScape prompts you during your first route, tap 'Allow' to enable the notification permission",
              "This allows the app to show a foreground service notification while you're on an active route",
              "You cannot dismiss this notification while actively tracking a route, as it ensures reliable location updates",
            ]}
          />

          {/* Troubleshooting Section */}
          <div className="mt-16">
            <h2 className="text-snow-white font-bold text-3xl font-catamaran mb-6">
              Troubleshooting
            </h2>

            <div className="bg-snow-white bg-opacity-5 border border-snow-white border-opacity-10 rounded-lg p-6 space-y-6">
              <div>
                <h3 className="text-snow-white font-semibold text-xl mb-2">
                  Location Shows as "Unknown"
                </h3>
                <p className="text-snow-white">
                  This usually means location permission is missing or location
                  services are disabled. Review the "Allow Location Access" and
                  "Enable Location Services" sections above. Also ensure GPS is
                  enabled on your device.
                </p>
              </div>

              <div>
                <h3 className="text-snow-white font-semibold text-xl mb-2">
                  Location Updates Stop After a Few Minutes
                </h3>
                <p className="text-snow-white">
                  This typically indicates battery optimization is too
                  aggressive. Follow the "Disable Battery Optimizations" section
                  above to allow SnowScape to run continuously in the
                  background.
                </p>
              </div>

              <div>
                <h3 className="text-snow-white font-semibold text-xl mb-2">
                  Still Having Issues?
                </h3>
                <p className="text-snow-white">
                  Try restarting your device and ensure SnowScape is updated to
                  the latest version. If problems persist, contact support for
                  assistance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </WithSnowFlakes>
  );
};

export default DeviceSettingsPage;

const SettingSection = ({
  id,
  title,
  description,
  steps,
  iosSteps,
  androidSteps,
}: {
  id: string;
  title: string;
  description: string;
  steps?: string[];
  iosSteps?: string[];
  androidSteps?: string[];
}) => {
  const platforms = [
    { name: "iOS", applicable: !!iosSteps },
    { name: "Android", applicable: !!androidSteps },
  ].filter((p) => p.applicable || !iosSteps || !androidSteps);

  return (
    <div
      id={id}
      className="mt-16 pb-12 border-b border-snow-white border-opacity-20"
    >
      <div className="mb-6">
        <h2 className="text-snow-white font-bold text-3xl font-catamaran mb-2">
          {title}
        </h2>
        <div className="flex flex-wrap items-center">
          {platforms
            .filter((p) => p.applicable)
            .map((platform) => (
              <PlatformBadge key={platform.name} platform={platform.name} />
            ))}
        </div>
      </div>

      <p className="text-snow-white text-lg leading-relaxed mb-6">
        {description}
      </p>

      {iosSteps && androidSteps ? (
        <ConfigurationTabs iosSteps={iosSteps} androidSteps={androidSteps} />
      ) : steps ? (
        <div className="bg-snow-white bg-opacity-5 border border-snow-white border-opacity-10 rounded-lg p-6">
          <h3 className="text-snow-white font-semibold text-xl mb-4">
            How to Configure:
          </h3>
          <ol className="text-snow-white space-y-3">
            {steps.map((step, index) => (
              <li key={index} className="flex">
                <span className="font-bold text-snow-green mr-4 min-w-max">
                  Step {index + 1}:
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : (
        <ConfigurationTabs iosSteps={iosSteps} androidSteps={androidSteps} />
      )}
    </div>
  );
};

const ConfigurationTabs = ({
  iosSteps,
  androidSteps,
}: {
  iosSteps?: string[];
  androidSteps?: string[];
}) => {
  const [activeTab, setActiveTab] = useState<"ios" | "android">(
    iosSteps ? "ios" : "android",
  );

  const showBothTabs = iosSteps && androidSteps;
  const steps = activeTab === "ios" ? iosSteps : androidSteps;

  return (
    <div className="bg-snow-white bg-opacity-5 border border-snow-white border-opacity-10 rounded-lg p-6">
      <h3 className="text-snow-white font-semibold text-xl mb-4">
        How to Configure:
      </h3>

      {showBothTabs && (
        <div className="flex gap-2 mb-6 border-b border-snow-white border-opacity-20">
          <button
            onClick={() => setActiveTab("ios")}
            className={`pb-3 px-4 font-semibold transition-colors ${
              activeTab === "ios"
                ? "text-snow-green border-b-2 border-snow-green"
                : "text-snow-white text-opacity-60 hover:text-opacity-100"
            }`}
          >
            iOS
          </button>
          <button
            onClick={() => setActiveTab("android")}
            className={`pb-3 px-4 font-semibold transition-colors ${
              activeTab === "android"
                ? "text-snow-green border-b-2 border-snow-green"
                : "text-snow-white text-opacity-60 hover:text-opacity-100"
            }`}
          >
            Android
          </button>
        </div>
      )}

      {steps && (
        <ol className="text-snow-white space-y-3">
          {steps.map((step, index) => (
            <li key={index} className="flex">
              <span className="font-bold text-snow-green mr-4 min-w-max">
                Step {index + 1}:
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

const PlatformBadge = ({ platform }: { platform: string }) => (
  <span
    className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ml-2 bg-snow-green text-black`}
  >
    {platform}
  </span>
);
